import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';

export const LatestNewsSection = () => {
  const [news, setNews] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadNews = async () => {
      try {
        const [newsRes, leaguesRes] = await Promise.all([
          api.getNews({ limit: 4 }),
          api.getLeagues()
        ]);
        setNews(newsRes.data);
        setLeagues(leaguesRes.data);
      } catch (error) {
        console.error('Error loading news:', error);
      } finally {
        setLoading(false);
      }
    };
    loadNews();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getLeagueName = (leagueId) => {
    const league = leagues.find(l => l.id === leagueId);
    return league?.name || '';
  };

  if (loading) {
    return (
      <section className="py-10 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded w-48 mb-6" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-gray-800 rounded-xl h-64" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (news.length === 0) {
    return null;
  }

  return (
    <section className="py-10 px-4" data-testid="latest-news-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Latest News</h2>
          <Link 
            to="/news" 
            className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1"
          >
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {news.slice(0, 4).map((article, index) => (
            <Link
              key={article.id}
              to={`/news/${article.slug}`}
              className={`group relative bg-gray-800/50 rounded-xl overflow-hidden border border-gray-700 hover:border-sky-500/50 transition-all ${
                index === 0 ? 'md:col-span-2 md:row-span-2' : ''
              }`}
            >
              {/* Image */}
              <div className={`relative overflow-hidden ${index === 0 ? 'h-full min-h-[320px]' : 'h-40'}`}>
                {article.featured_image ? (
                  <img 
                    src={resolveMediaUrl(article.featured_image)} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-sky-900 to-blue-900" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                
                {/* Content Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  {article.league_ids?.length > 0 && (
                    <Badge className="bg-sky-500/80 text-white text-xs mb-2">
                      {getLeagueName(article.league_ids[0])}
                    </Badge>
                  )}
                  <h3 className={`text-white font-semibold group-hover:text-sky-300 transition-colors ${
                    index === 0 ? 'text-xl md:text-2xl' : 'text-sm'
                  } line-clamp-2`}>
                    {article.title}
                  </h3>
                  <p className="text-gray-400 text-xs mt-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(article.published_at || article.created_at)}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
